// src/services/api/itineraryApi.ts
import { Itinerary } from '../dataPersistence/types';
import { authTokenManager } from '../../utils/authTokenManager';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  statusCode?: number;
}

/**
 * API service for communicating with the itinerary endpoints
 */
export class ItineraryApiService {
  private baseUrl: string;
  
  constructor() {
    this.baseUrl = `${import.meta.env.VITE_API_BASE_URL || ''}/api/user-data/itineraries`;
  }
  
  /**
   * Get authentication headers for API requests
   */
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    // Get the current auth token
    const token = await authTokenManager.getValidToken();
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }
  
  /**
   * Get all itineraries for the current user
   */
  public async getAllItineraries(): Promise<ApiResponse<Itinerary[]>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('[ItineraryApiService] Error fetching itineraries:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Get a specific itinerary by ID
   */
  public async getItinerary(id: string): Promise<ApiResponse<Itinerary>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error(`[ItineraryApiService] Error fetching itinerary ${id}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Create a new itinerary on the server
   */
/*   public async createItinerary(itinerary: Omit<Itinerary, 'userId'>): Promise<ApiResponse<Itinerary>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(itinerary)
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('[ItineraryApiService] Error creating itinerary:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  } */
  /**
 * Create a new itinerary on the server
 */

 /*  public async createItinerary(itinerary: Omit<Itinerary, 'userId'>): Promise<ApiResponse<Itinerary>> {
    console.log('[ItineraryApiService] Creating itinerary:', JSON.stringify(itinerary, null, 2)); */
    /* try {
      const headers = await this.getHeaders();
      
      // Add detailed logging before sending the request
      console.log('[ItineraryApiService] Creating itinerary with data:', JSON.stringify(itinerary, null, 2));
      console.log('[ItineraryApiService] Request URL:', this.baseUrl);
      console.log('[ItineraryApiService] Headers:', JSON.stringify(headers, null, 2));
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(itinerary)
      });
      
      // Log response status
      console.log(`[ItineraryApiService] Response status: ${response.status} ${response.statusText}`); */
      
      /* try {
        const headers = await this.getHeaders();
        
        // Add explicit debugging logs
        console.log('[ItineraryApiService] Creating itinerary:', {
          url: this.baseUrl,
          method: 'POST',
          headerKeys: Object.keys(headers),
          hasAuthHeader: !!headers['Authorization']
        });
        
        // Make sure the URL is correctly formed with no extra or missing slashes
        const cleanUrl = this.baseUrl.replace(/\/+$/, '');
        
        const response = await fetch(cleanUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify(itinerary),
          // Add these options to help with CORS
          mode: 'cors',
          credentials: 'same-origin'
        }); */
        
    public async createItinerary(itinerary: Omit<Itinerary, 'userId'>): Promise<ApiResponse<Itinerary>> {
      try {
        const headers = await this.getHeaders();
        
        console.log('[ItineraryApiService] Attempting with exact curl equivalent format');
        
        // Simplify the data to match our successful curl request
        const simplifiedData = {
          tripDetails: {
            destination: itinerary.tripDetails.destination,
            startDate: itinerary.tripDetails.startDate,
            endDate: itinerary.tripDetails.endDate,
            travelGroup: itinerary.tripDetails.travelGroup || 'Solo traveler'
          },
          days: itinerary.days || [{
            dayNumber: 1,
            date: new Date().toISOString(),
            activities: []
          }],
          // Add budget data to API request
          budgetSummary: itinerary.budgetSummary || null
        };
        
        console.log('[ItineraryApiService] Sending simplified data:', JSON.stringify(simplifiedData, null, 2));
        
        const response = await fetch(this.baseUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': headers.Authorization
          },
          body: JSON.stringify(simplifiedData)
        });  
        console.log(`[ItineraryApiService] Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        // Try to get the error message from the response
        let errorDetail = '';
        try {
          const errorData = await response.json();
          errorDetail = JSON.stringify(errorData);
        } catch (e) {
          errorDetail = 'Could not parse error response';
        }
        
        console.error(`[ItineraryApiService] Server error: ${errorDetail}`);
        
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText} - ${errorDetail}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      console.log('[ItineraryApiService] Successfully created itinerary:', data.id);
      return { success: true, data };
    } catch (error) {
      console.error('[ItineraryApiService] Error creating itinerary:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  /**
   * Update an existing itinerary
   */
  public async updateItinerary(id: string, updates: Partial<Itinerary>): Promise<ApiResponse<Itinerary>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error(`[ItineraryApiService] Error updating itinerary ${id}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Delete an itinerary
   */
  public async deleteItinerary(id: string): Promise<ApiResponse<void>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      return { success: true };
    } catch (error) {
      console.error(`[ItineraryApiService] Error deleting itinerary ${id}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

// Create singleton instance
export const itineraryApiService = new ItineraryApiService();
